import { useModal, withModalConditionalRender } from '@billy/design-system'
import { Trash2Icon } from 'lucide-react'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { VoidBillConfirmationModal as VoidBillConfirmationModalComponent } from '@features/bills/elements/VoidBillConfirmationModal'
import { BillStatus } from '@features/bills/enums/billStatus'

import { isBillDeletable, isBillVoidable } from '../../../../../../utils/isBillDeletable'
import { useDeleteBillAction } from '../../../../../BillEditableForm/hooks/useDeleteBillAction'
import { DeleteBillConfirmationModal as DeleteBillConfirmationModalComponent } from '../../../../../DeleteBillConfirmationModal'
import { BillsTableRow } from '../../../../types/billsTableRow'
import { BillsTableActionType } from '../../enums/billsTableActionType'
import { BillsTableAction } from '../../types/billsTableAction'
import { getVoidBillDisabledReason } from '../../utils/getVoidBillDisabledReason'

const DeleteBillConfirmationModal = withModalConditionalRender(DeleteBillConfirmationModalComponent)
const VoidBillConfirmationModal = withModalConditionalRender(VoidBillConfirmationModalComponent)

const getDeleteBillModalId = (billId: string) => `${billId}-bills-table-delete-bill`
const getVoidBillModalId = (billId: string) => `${billId}-bills-table-void-bill`

/**
 * Drafts are deleted, approved bills are voided — both are presented as "Void" (same as the legacy bills table).
 *
 * @todo Handle localization
 * @see https://linear.app/shine-co/issue/EXP-357/web-add-localization
 */
export const useVoidBillAction = (row: BillsTableRow): BillsTableAction => {
  const { t } = useTranslation()
  const isDraft = row.derivedStatus === BillStatus.Draft
  const deleteModalId = getDeleteBillModalId(row.id)
  const voidModalId = getVoidBillModalId(row.id)

  const { open: openDeleteConfirmationModal, close: closeDeleteConfirmationModal } = useModal(deleteModalId)
  const { open: openVoidConfirmationModal } = useModal(voidModalId)

  const { deleteBill, isDeleting } = useDeleteBillAction({
    onSuccess: () => {
      closeDeleteConfirmationModal()
    },
  })

  const handleDeleteBill = useCallback(() => {
    deleteBill(row.id)
  }, [deleteBill, row.id])

  // Bills synced to the CPA can only be voided/deleted if the sync was rejected for specific reasons
  const integrationStatus = row.syncStatus ? [row.syncStatus] : undefined
  const isEnabled = isDraft
    ? isBillDeletable({ bill: row.bill, integrationStatus })
    : isBillVoidable({ bill: row.bill, integrationStatus })

  return {
    type: BillsTableActionType.Void,
    label: 'Void',
    icon: Trash2Icon,
    onClick: isDraft ? openDeleteConfirmationModal : openVoidConfirmationModal,
    disabled: !isEnabled,
    disabledReason: isEnabled ? null : getVoidBillDisabledReason(row.syncStatus, t),
    confirmationModal: isDraft ? (
      <DeleteBillConfirmationModal id={deleteModalId} onOk={handleDeleteBill} isDeleting={isDeleting} />
    ) : (
      <VoidBillConfirmationModal id={voidModalId} billId={row.id} />
    ),
  }
}
